import { ReactNode, useCallback, useMemo, useState } from 'react';
import {
  AiBudgetStatusFragmentFragment,
  useAiBudgetStatusLiveSubscription,
  useAiBudgetStatusQuery,
} from 'graphql-common';
import { useTabActivity } from '@lib/hooks/useTabActivity';
import { logger } from '@lib/services/logger';
import { AiBudgetContextProvider } from 'components/AiAssistant/AiBudgetContext';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';

type Props = {
  // Whether the AI budget may be read at all — the company plan allows
  // AiAssistance, EDM isn't plan-locked, and this user/team isn't individually
  // restricted. Both the query and the subscription error without that, so
  // both stay off.
  enabled: boolean;
  children: ReactNode;
};

// The company's AI budget, read once for the whole dashboard so every AI
// affordance answers "may the user spend right now" (BE guide §10) from the
// same live value: the chat composer, the per-field autofill triggers on the
// form / view / list, and the import form's AI enhancements.
//
// Freshness is the subscription's job, not the callers'. `aiBudgetStatus`
// replays the current snapshot on subscribe and then pushes a frame after every
// metered action — a chat turn, a field generation, background document
// indexing — plus whenever a system admin changes the cap. So the meter and the
// block state follow spend the user didn't cause (another member's chat, an
// import's detection child) without anyone remembering to refetch.
//
// The query stays as the reliable floor: it seeds the first paint if the cable
// is slow, and `refetch` re-syncs the exact value after events that suggest the
// stream may have missed something (tab focus, a settled generation).
export default function AiBudgetProvider({ enabled, children }: Props) {
  const [liveStatus, setLiveStatus] =
    useState<AiBudgetStatusFragmentFragment | null>(null);

  const { data, refetch } = useAiBudgetStatusQuery({
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  useAiBudgetStatusLiveSubscription({
    skip: !enabled,
    fetchPolicy: 'no-cache',
    onData: ({ data: frame }) => {
      const status = frame.data?.aiBudgetStatus;
      if (status) setLiveStatus(status);
    },
    onError: (error) => {
      // The query and its refetches carry the meter on their own, so a dead
      // cable costs freshness, not correctness.
      logger.debug('AiBudget: subscription error', { error });
    },
  });

  const refetchBudget = useCallback(() => {
    if (enabled) refetch();
  }, [enabled, refetch]);

  // A backgrounded tab misses frames whenever the cable drops, and reconnecting
  // replays only the snapshot at that moment — cheap to just ask.
  useTabActivity({ onFocus: refetchBudget });

  // Whichever snapshot was computed last wins, so a refetch that resolves after
  // a newer frame (or vice versa) can't roll the meter backwards.
  const queryStatus = data?.data ?? null;
  const status = useMemo(() => {
    if (!liveStatus) return queryStatus;
    if (!queryStatus) return liveStatus;
    const computedAt = (snapshot: AiBudgetStatusFragmentFragment) =>
      Date.parse(snapshot.updatedAt) || 0;
    return computedAt(queryStatus) > computedAt(liveStatus)
      ? queryStatus
      : liveStatus;
  }, [liveStatus, queryStatus]);

  const remaining = status?.remainingCredits ?? null;
  const total = status?.totalCredits ?? null;
  const enforced = status?.enforced ?? false;
  const blockedReason = status?.blockedReason ?? null;
  // Stable identity while the numbers are unchanged, so consumers that key
  // memos on `credits` (the document form fields) don't recompute every frame.
  const credits = useMemo<AiCredits | null>(
    () =>
      enforced && remaining != null ? { remaining, total: total ?? 0 } : null,
    [enforced, remaining, total],
  );

  const value = useMemo(
    () => ({ credits, blockedReason, refetch: refetchBudget }),
    [credits, blockedReason, refetchBudget],
  );

  return (
    <AiBudgetContextProvider value={value}>{children}</AiBudgetContextProvider>
  );
}
